import { CheckCircle2, LoaderCircle } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { checkDeviceEndpoint } from '../../api/devices';
import { ConnectionTest, PasswordField, type ConnectionState } from './FormControls';
import type { DeviceFormProps } from './deviceTypes';

interface ServerFormState {
  name: string;
  ipAddress: string;
  username: string;
  password: string;
  port: string;
  os: 'windows-server' | 'linux' | 'other';
  serverType: 'physical' | 'virtual';
  connection: 'ssh' | 'rdp' | 'winrm' | 'https' | 'other';
  role: string;
  location: string;
}

const defaultPortFor: Record<ServerFormState['connection'], string> = {
  ssh: '22',
  rdp: '3389',
  winrm: '5985',
  https: '443',
  other: '443',
};

const initialState: ServerFormState = {
  name: '',
  ipAddress: '',
  username: '',
  password: '',
  port: '22',
  os: 'linux',
  serverType: 'physical',
  connection: 'ssh',
  role: '',
  location: '',
};

export function ServerForm({ onSubmit, onCancel }: DeviceFormProps) {
  const [form, setForm] = useState<ServerFormState>(initialState);
  const [error, setError] = useState('');
  const [connectionState, setConnectionState] = useState<ConnectionState>('idle');
  const [connectionMessage, setConnectionMessage] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);

  const update = <Key extends keyof ServerFormState>(key: Key, value: ServerFormState[Key]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setConnectionState('idle');
    setConnectionMessage('');
    setError('');
  };

  const updateConnection = (connection: ServerFormState['connection']) => {
    setForm((current) => ({ ...current, connection, port: defaultPortFor[connection] }));
    setConnectionState('idle');
    setConnectionMessage('');
    setError('');
  };

  const validate = () => {
    if (!form.name.trim()) { setError('Укажите имя сервера.'); return false; }
    if (!form.ipAddress.trim()) { setError('Укажите IP-адрес или имя хоста.'); return false; }
    if (Number(form.port) < 1 || Number(form.port) > 65535) { setError('Порт должен быть от 1 до 65535.'); return false; }
    return true;
  };

  const handleConnectionCheck = async () => {
    setError('');
    if (!validate()) return;
    setConnectionState('checking');
    setConnectionMessage('');
    try {
      const result = await checkDeviceEndpoint({
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port),
      });
      setConnectionState(result.reachable ? 'success' : 'error');
      setConnectionMessage(
        result.reachable
          ? `Порт ${form.port} отвечает за ${Math.max(1, Math.round(result.durationMs))} мс.`
          : result.message || `Порт ${form.port} недоступен. Проверьте адрес и брандмауэр.`,
      );
    } catch (checkError) {
      setConnectionState('error');
      setConnectionMessage(checkError instanceof Error ? checkError.message : 'Не удалось выполнить проверку.');
    }
  };

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError('');
    if (!validate()) return;
    setIsSubmitting(true);
    try {
      const osLabel = { 'windows-server': 'Windows Server', linux: 'Linux', other: 'Other' }[form.os];
      await onSubmit({
        deviceType: 'Server',
        name: form.name.trim(),
        ipAddress: form.ipAddress.trim(),
        port: Number(form.port),
        osVersion: osLabel,
        role: [form.serverType === 'virtual' ? 'VM' : 'Physical', form.role.trim()].filter(Boolean).join(' · ').trim() || undefined,
        location: form.location.trim() || undefined,
      });
      onCancel();
    } catch (submitError) {
      setError(submitError instanceof Error ? submitError.message : 'Не удалось добавить сервер.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="camera-form" onSubmit={handleSubmit}>
      <div className="camera-form__scroller">
        <div className="camera-form__primary">
          <label className="field-group">
            <span>Имя сервера</span>
            <input required type="text" maxLength={200} value={form.name} onChange={(event) => update('name', event.target.value)} placeholder="Например, SRV-DB-01" />
          </label>
          <label className="field-group">
            <span>IP-адрес / хост</span>
            <input required type="text" inputMode="url" value={form.ipAddress} onChange={(event) => update('ipAddress', event.target.value)} placeholder="192.168.1.10" autoCapitalize="none" autoCorrect="off" spellCheck={false} />
          </label>
          <div className="camera-form__grid">
            <label className="field-group">
              <span>Тип сервера</span>
              <select value={form.serverType} onChange={(event) => update('serverType', event.target.value as ServerFormState['serverType'])}>
                <option value="physical">Физический</option>
                <option value="virtual">Виртуальный</option>
              </select>
            </label>
            <label className="field-group">
              <span>Операционная система</span>
              <select value={form.os} onChange={(event) => update('os', event.target.value as ServerFormState['os'])}>
                <option value="windows-server">Windows Server</option>
                <option value="linux">Linux</option>
                <option value="other">Другая</option>
              </select>
            </label>
          </div>
          <label className="field-group">
            <span>Подключение</span>
            <select value={form.connection} onChange={(event) => updateConnection(event.target.value as ServerFormState['connection'])}>
              <option value="ssh">SSH</option>
              <option value="rdp">RDP</option>
              <option value="winrm">WinRM</option>
              <option value="https">HTTPS</option>
              <option value="other">Другое</option>
            </select>
          </label>
          <label className="field-group">
            <span>Порт</span>
            <input required type="number" min="1" max="65535" inputMode="numeric" value={form.port} onChange={(event) => update('port', event.target.value)} />
          </label>
          <label className="field-group">
            <span>Логин</span>
            <input type="text" value={form.username} onChange={(event) => update('username', event.target.value)} autoComplete="username" autoCapitalize="none" autoCorrect="off" spellCheck={false} placeholder="root" />
          </label>
          <PasswordField label="Пароль" value={form.password} onChange={(value) => update('password', value)} />
          <label className="field-group">
            <span>Роль / назначение</span>
            <input type="text" maxLength={100} value={form.role} onChange={(event) => update('role', event.target.value)} placeholder="База данных, веб, бэкап…" />
          </label>
          <label className="field-group">
            <span>Расположение</span>
            <input type="text" maxLength={200} value={form.location} onChange={(event) => update('location', event.target.value)} placeholder="Серверная, стойка 3" />
          </label>
        </div>

        <button className="button button--secondary camera-form__check" type="button" onClick={() => void handleConnectionCheck()} disabled={connectionState === 'checking'}>
          {connectionState === 'checking' ? <LoaderCircle className="spin" size={18} /> : <CheckCircle2 size={18} />}
          {connectionState === 'checking' ? 'Проверяем…' : 'Проверить подключение'}
        </button>

        <ConnectionTest
          state={connectionState}
          successTitle="Сервер доступен"
          successMessage={connectionMessage}
          errorTitle="Не удалось подключиться"
          errorMessage={connectionMessage}
        />

        <p className="camera-form__security-note">Доступность сервера проверяется по сети. Системные метрики без агента не собираются.</p>
        {error && <p className="camera-form__error" role="alert">{error}</p>}
      </div>
      <footer className="camera-dialog__actions">
        <button className="button button--secondary" type="button" onClick={onCancel}>Отмена</button>
        <button className="button button--primary" type="submit" disabled={isSubmitting}>{isSubmitting ? 'Добавление…' : 'Добавить сервер'}</button>
      </footer>
    </form>
  );
}
